import { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

interface NewsletterCTAProps {
  variant?: 'default' | 'compact';
}

export const NewsletterCTA = ({ variant = 'default' }: NewsletterCTAProps) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Placeholder — store in localStorage until a real backend is wired
    const existing = JSON.parse(localStorage.getItem('newsletter_emails') || '[]');
    existing.push({ email: email.trim(), date: new Date().toISOString() });
    localStorage.setItem('newsletter_emails', JSON.stringify(existing));

    setSubmitted(true);
    setEmail('');
    toast({ title: t('newsletter.success'), description: t('newsletter.successDesc') });
  };

  if (submitted) {
    return (
      <div className={variant === 'compact' ? 'py-8' : 'py-12 md:py-16'}>
        <div className="text-center">
          <p className="text-[10px] tracking-luxury uppercase text-accent mb-2">
            {t('newsletter.thankYou')}
          </p>
          <p className="text-sm text-muted-foreground/80 font-light">
            {t('newsletter.confirmText')}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div>
        <p className="text-[10px] tracking-luxury uppercase text-primary-foreground/50 mb-4">
          {t('newsletter.heading')}
        </p>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('newsletter.placeholder')}
            className="flex-1 px-3 py-2 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground text-sm placeholder:text-primary-foreground/40 focus:outline-none focus:border-primary-foreground/40 transition-colors duration-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-accent text-accent-foreground text-sm tracking-luxury uppercase hover:bg-accent/90 transition-colors duration-500"
          >
            {t('newsletter.cta')}
          </button>
        </form>
        <p className="text-[10px] text-primary-foreground/30 mt-2 font-light">
          {t('newsletter.privacy')}
        </p>
      </div>
    );
  }

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-2xl mx-auto text-center border border-border/40 bg-secondary/20 p-8 md:p-12">
        <div className="w-10 h-10 border border-accent/30 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-5 h-5 text-accent" />
        </div>
        <span className="text-[10px] tracking-widest uppercase text-accent/80 block mb-2">Stay Connected</span>
        <h3 className="font-serif text-2xl md:text-3xl font-medium text-foreground mb-3">
          {t('newsletter.heading')}
        </h3>
        <p className="text-sm text-muted-foreground/80 font-light mb-8 max-w-md mx-auto leading-relaxed">
          {t('newsletter.description')}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('newsletter.placeholder')}
            className="flex-1 px-4 py-3 border border-border/40 bg-background text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50 transition-colors duration-500"
          />
          <button
            type="submit"
            className="px-6 py-3 bg-accent text-accent-foreground text-[10px] tracking-luxury uppercase font-normal hover:bg-accent/90 transition-colors duration-700"
          >
            {t('newsletter.cta')}
          </button>
        </form>
        <p className="text-[10px] text-muted-foreground/50 mt-4 font-light">
          {t('newsletter.privacy')}
        </p>
      </div>
    </section>
  );
};
